/**
 * /calls — real-time call observability dashboard.
 *
 * Top stats · live call cards with streaming transcripts · recent calls table
 * with a click-to-open drawer for the full transcript and tool-call timeline.
 *
 * Data flow:
 *   - On mount, GET /calls for the recent list (snapshot).
 *   - Open a single WebSocket; every event is applied to local state.
 *   - When a call_start arrives, we add a live card. When call_end arrives,
 *     we move it to the recent list.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CallEventBase,
  CallMeta,
  CallTurn,
  deleteAllCalls,
  deleteCall,
  fetchCall,
  fetchRecentCalls,
  openEventsWebSocket,
} from "./lib/calls";

interface LiveCall {
  callId: string;
  path: string;
  fromNumber: string;
  channel: string;
  startedAt: number;     // ms
  turns: LiveTurn[];     // append-only
}
interface LiveTurn {
  type: string;
  ts: string;
  data: Record<string, unknown>;
}

const PATH_LABELS: Record<string, { label: string; pillBg: string; pillFg: string }> = {
  A: { label: "Path A · Lex", pillBg: "#FAEEDA", pillFg: "#854F0B" },
  B: { label: "Path B · Pipecat", pillBg: "#E1F5EE", pillFg: "#085041" },
  C: { label: "Path C · Nova Sonic", pillBg: "#EEEDFE", pillFg: "#3C3489" },
};


export function CallsPage(): JSX.Element {
  const [recent, setRecent] = useState<CallMeta[]>([]);
  const [live, setLive] = useState<Record<string, LiveCall>>({});
  const [openCallId, setOpenCallId] = useState<string | null>(null);
  const [openCall, setOpenCall] = useState<{ meta: CallMeta | null; turns: CallTurn[] } | null>(null);
  const [updatedAt, setUpdatedAt] = useState<number>(Date.now());
  const [, forceTick] = useState(0);

  // Re-render every second so the "01:24" clocks on live cards update
  useEffect(() => {
    const id = window.setInterval(() => forceTick((t) => t + 1), 1000);
    return () => window.clearInterval(id);
  }, []);

  // Initial load
  useEffect(() => {
    void fetchRecentCalls(50).then(setRecent);
  }, []);

  // WebSocket subscribe
  useEffect(() => {
    const conn = openEventsWebSocket((e: CallEventBase) => {
      setUpdatedAt(Date.now());
      const callId = e.callId;
      const pathId = String(e.path || "?");
      const data = e.data || {};

      if (e.type === "call_start") {
        setLive((prev) => {
          if (prev[callId]) return prev;
          return {
            ...prev,
            [callId]: {
              callId,
              path: pathId,
              fromNumber: String(data.from || ""),
              channel: String(data.channel || "voice"),
              startedAt: Date.now(),
              turns: [],
            },
          };
        });
        return;
      }

      if (e.type === "call_end") {
        // Move live → recent. Keep most fields from live, layer in end data.
        setLive((prev) => {
          const cur = prev[callId];
          if (!cur) return prev;
          const next = { ...prev };
          delete next[callId];
          const ended: CallMeta = {
            callId,
            path: cur.path,
            startedAt: new Date(cur.startedAt).toISOString(),
            endedAt: e.ts,
            durationMs: typeof data.durationMs === "number" ? data.durationMs : Date.now() - cur.startedAt,
            status: "ended",
            outcome: String(data.outcome || ""),
            fromNumber: cur.fromNumber,
            channel: cur.channel,
          };
          setRecent((r) => [ended, ...r].slice(0, 100));
          return next;
        });
        return;
      }

      // Any other event → append to that call's live turns (if active)
      setLive((prev) => {
        const cur = prev[callId];
        if (!cur) {
          // No call_start seen — lazily synthesise one so we don't drop events
          return {
            ...prev,
            [callId]: {
              callId,
              path: pathId,
              fromNumber: "",
              channel: "voice",
              startedAt: Date.now(),
              turns: [{ type: e.type, ts: e.ts, data }],
            },
          };
        }
        return {
          ...prev,
          [callId]: { ...cur, turns: [...cur.turns, { type: e.type, ts: e.ts, data }] },
        };
      });
    });

    return () => conn.close();
  }, []);

  // Drawer fetch
  useEffect(() => {
    if (!openCallId) {
      setOpenCall(null);
      return;
    }
    void fetchCall(openCallId).then((c) => setOpenCall({ meta: c.call, turns: c.turns }));
  }, [openCallId]);

  // Delete handlers
  const handleDeleteCall = useCallback(async (callId: string) => {
    const ok = window.confirm(`Delete this call?\n\n${callId.slice(0, 16)}…\n\nThis removes the full transcript and cannot be undone.`);
    if (!ok) return;
    const success = await deleteCall(callId);
    if (success) {
      setRecent((r) => r.filter((c) => c.callId !== callId));
      setLive((prev) => {
        const next = { ...prev };
        delete next[callId];
        return next;
      });
      if (openCallId === callId) setOpenCallId(null);
    } else {
      window.alert("Couldn't delete that call.");
    }
  }, [openCallId]);

  const handleDeleteAll = useCallback(async () => {
    const total = recent.length + Object.keys(live).length;
    if (total === 0) return;
    const ok = window.confirm(`Delete ALL ${total} calls?\n\nEvery call record and transcript will be removed. This cannot be undone.`);
    if (!ok) return;
    const deleted = await deleteAllCalls();
    setRecent([]);
    setLive({});
    setOpenCallId(null);
    window.alert(`Deleted ${deleted} record${deleted === 1 ? "" : "s"}.`);
  }, [recent.length, live]);

  // Stats
  const todayKey = useMemo(() => new Date().toISOString().slice(0, 10), [updatedAt]);
  const liveList = useMemo(() => Object.values(live).sort((a, b) => b.startedAt - a.startedAt), [live]);
  const callsToday = useMemo(() => {
    const fromRecent = recent.filter((c) => (c.startedAt || "").startsWith(todayKey)).length;
    const fromLive = liveList.length;
    return fromRecent + fromLive;
  }, [recent, liveList, todayKey]);
  const avgDuration = useMemo(() => {
    const durations = recent.map((c) => c.durationMs || 0).filter((d) => d > 0);
    if (!durations.length) return "—";
    const ms = durations.reduce((a, b) => a + b, 0) / durations.length;
    return fmtDuration(ms);
  }, [recent]);
  const resolutionRate = useMemo(() => {
    if (!recent.length) return "—";
    const resolved = recent.filter((c) => !!c.outcome && c.outcome !== "Caller hung up" && c.outcome !== "Fallback").length;
    return `${Math.round((resolved / recent.length) * 100)}%`;
  }, [recent]);

  return (
    <div className="calls-page">
      <header className="calls-header">
        <div>
          <h1 className="calls-title">Live calls</h1>
          <div className="calls-sub">voicedemos.polysage.tech / calls</div>
        </div>
        <div className="calls-status">
          <span className="live-dot" /> Streaming
          <span className="updated-ago">Updated {fmtAgo(updatedAt)}</span>
          <a className="calls-back-link" href="/">← Back to demo</a>
        </div>
      </header>

      <div className="stat-row">
        <StatCard label="Calls today" value={String(callsToday)} />
        <StatCard
          label="Active now"
          value={String(liveList.length)}
          icon={liveList.length ? <span className="live-dot" /> : null}
        />
        <StatCard label="Avg duration" value={avgDuration} />
        <StatCard label="Resolution rate" value={resolutionRate} />
      </div>

      <h2 className="section-label">Live now</h2>
      {liveList.length === 0 ? (
        <div className="empty-live">No active calls. Place a call to +1 (855) 340-9717 (Path B) or use the demo page to start one.</div>
      ) : (
        <div className="live-grid">
          {liveList.map((c) => (
            <LiveCallCard key={c.callId} call={c} onOpen={() => setOpenCallId(c.callId)} />
          ))}
        </div>
      )}

      <div className="section-row">
        <h2 className="section-label" style={{ margin: 0 }}>Recent calls</h2>
        {(recent.length > 0 || Object.keys(live).length > 0) && (
          <button className="danger-btn" onClick={handleDeleteAll}>
            Delete all
          </button>
        )}
      </div>
      <div className="recent-table-wrap">
        <table className="recent-table">
          <thead>
            <tr>
              <th>Time</th>
              <th>Path</th>
              <th>From</th>
              <th>Duration</th>
              <th>Outcome</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {recent.length === 0 ? (
              <tr><td colSpan={6} className="recent-empty">No calls yet.</td></tr>
            ) : recent.map((c) => (
              <tr key={c.callId} className="row-clickable">
                <td className="cell-muted" onClick={() => setOpenCallId(c.callId)}>{fmtTime(c.startedAt)}</td>
                <td onClick={() => setOpenCallId(c.callId)}><PathPill path={String(c.path)} /></td>
                <td className="cell-mono" onClick={() => setOpenCallId(c.callId)}>{c.fromNumber || (c.channel === "web" ? "web" : "—")}</td>
                <td className="cell-mono" onClick={() => setOpenCallId(c.callId)}>{c.durationMs ? fmtDuration(c.durationMs) : "—"}</td>
                <td onClick={() => setOpenCallId(c.callId)}>{c.outcome || "—"}</td>
                <td className="cell-right">
                  <button className="open-btn" onClick={(e) => { e.stopPropagation(); setOpenCallId(c.callId); }}>Open</button>
                  <button className="danger-btn-sm" onClick={(e) => { e.stopPropagation(); void handleDeleteCall(c.callId); }} aria-label="Delete this call">
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {openCallId && (
        <CallDrawer
          callId={openCallId}
          data={openCall}
          onClose={() => setOpenCallId(null)}
          onDelete={() => handleDeleteCall(openCallId)}
        />
      )}
    </div>
  );
}


// --------------------------------------------------------------------------- //
// Sub-components                                                              //
// --------------------------------------------------------------------------- //
function StatCard({ label, value, icon }: { label: string; value: string; icon?: React.ReactNode }) {
  return (
    <div className="stat-card">
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}{icon ? <span className="stat-icon">{icon}</span> : null}</div>
    </div>
  );
}

function PathPill({ path }: { path: string }) {
  const meta = PATH_LABELS[path] || { label: `Path ${path}`, pillBg: "#eee", pillFg: "#333" };
  return (
    <span className="path-pill" style={{ background: meta.pillBg, color: meta.pillFg }}>
      Path {path}
    </span>
  );
}

function LiveCallCard({ call, onOpen }: { call: LiveCall; onOpen: () => void }) {
  const meta = PATH_LABELS[call.path] || { label: `Path ${call.path}`, pillBg: "#eee", pillFg: "#333" };
  const elapsed = Math.max(0, Date.now() - call.startedAt);
  // Show the last ~6 events
  const tail = call.turns.slice(-12);
  return (
    <div className="live-card" onClick={onOpen}>
      <div className="live-card-head">
        <div className="live-card-meta">
          <span className="path-pill" style={{ background: meta.pillBg, color: meta.pillFg }}>{meta.label}</span>
          <span className="live-card-from">{call.fromNumber || (call.channel === "web" ? "web" : "—")}</span>
        </div>
        <div className="live-card-clock">
          <span className="live-dot" /> <span className="mono">{fmtDuration(elapsed)}</span>
        </div>
      </div>
      <div className="live-card-stream">
        {tail.length === 0 ? (
          <div className="dim italic">Connecting…</div>
        ) : tail.map((t, i) => <TurnLine key={i} t={t} />)}
      </div>
    </div>
  );
}

function CallDrawer({ callId, data, onClose, onDelete }: { callId: string; data: { meta: CallMeta | null; turns: CallTurn[] } | null; onClose: () => void; onDelete: () => void }) {
  return (
    <div className="drawer-backdrop" onClick={onClose}>
      <aside className="drawer" onClick={(e) => e.stopPropagation()}>
        <div className="drawer-head">
          <div>
            <div className="drawer-title">Call {callId.slice(0, 12)}…</div>
            {data?.meta && (
              <div className="drawer-sub">
                <PathPill path={String(data.meta.path)} />
                <span className="drawer-sub-item">{data.meta.fromNumber || data.meta.channel || "—"}</span>
                <span className="drawer-sub-item">{data.meta.durationMs ? fmtDuration(data.meta.durationMs) : "—"}</span>
                <span className="drawer-sub-item">{data.meta.outcome || (data.meta.status === "active" ? "active" : "—")}</span>
              </div>
            )}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="danger-btn-sm" onClick={onDelete}>Delete</button>
            <button className="drawer-close" onClick={onClose}>Close</button>
          </div>
        </div>
        <div className="drawer-body">
          {!data ? (
            <div className="dim italic">Loading…</div>
          ) : data.turns.length === 0 ? (
            <div className="dim italic">No transcript captured.</div>
          ) : (
            data.turns.map((t, i) => <TurnLine key={i} t={t as LiveTurn} />)
          )}
        </div>
      </aside>
    </div>
  );
}

function TurnLine({ t }: { t: LiveTurn }) {
  const d = t.data || {};
  if (t.type === "user_turn") {
    return <div className="turn turn-user"><span className="role">Caller</span> <span className="text">{String(d.text || "")}</span></div>;
  }
  if (t.type === "agent_turn") {
    return <div className="turn turn-agent"><span className="role">Agent</span> <span className="text">{String(d.text || "")}</span></div>;
  }
  if (t.type === "tool_call") {
    return <div className="turn turn-tool"><span className="role">tool ·</span> {String(d.name || "")} <span className="dim">{argsPreview(d.args)}</span></div>;
  }
  if (t.type === "tool_result") {
    return <div className="turn turn-tool"><span className="role">result ·</span> {String(d.name || "")} <span className="dim">{String(d.result || "").slice(0, 120)}</span></div>;
  }
  if (t.type === "intent") {
    return <div className="turn turn-intent"><span className="role">intent ·</span> {String(d.name || "")} <span className="dim">conf {fmt2(d.confidence)}</span></div>;
  }
  if (t.type === "slot") {
    return <div className="turn turn-intent"><span className="role">slot ·</span> {String(d.name || "")} = {String(d.value || "")}</div>;
  }
  return <div className="turn"><span className="dim">{t.type}</span> {JSON.stringify(d).slice(0, 120)}</div>;
}


// --------------------------------------------------------------------------- //
// Formatting helpers                                                          //
// --------------------------------------------------------------------------- //
function fmtDuration(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return `${m.toString().padStart(2, "0")}:${rem.toString().padStart(2, "0")}`;
}
function fmtTime(iso?: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}
function fmtAgo(updatedAt: number): string {
  const s = Math.max(0, Math.floor((Date.now() - updatedAt) / 1000));
  if (s < 5) return "just now";
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  return `${m}m ago`;
}
function argsPreview(a: unknown): string {
  if (!a || typeof a !== "object") return "";
  try {
    return JSON.stringify(a).slice(0, 80);
  } catch {
    return "";
  }
}
function fmt2(x: unknown): string {
  const n = typeof x === "number" ? x : Number(x);
  if (Number.isFinite(n)) return n.toFixed(2);
  return "—";
}
